import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DistribuicaoParams {
  formularioId: string;
  tipoDestinatario: 'usuario' | 'grupo' | 'role' | 'client' | 'todos';
  destinatarioId?: string;
  destinatarioRole?: string;
  obrigatorio: boolean;
  dataLimite?: Date;
  mensagemPersonalizada?: string;
}

export function useFormularioDistribuicao(formularioId: string | undefined) {
  const queryClient = useQueryClient();

  // Buscar distribuições do formulário
  const { data: distribuicoes, isLoading } = useQuery({
    queryKey: ['formulario-distribuicoes', formularioId],
    queryFn: async () => {
      if (!formularioId) return [];

      const { data, error } = await supabase
        .from('formulario_distribuicoes')
        .select('*')
        .eq('formulario_id', formularioId)
        .order('criado_em', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!formularioId,
  });

  // Buscar estatísticas de uma distribuição
  const getEstatisticas = async (distribuicaoId: string) => {
    const { data: notificacoes, error } = await supabase
      .from('formulario_notificacoes')
      .select('id, respondida')
      .eq('distribuicao_id', distribuicaoId);

    if (error) throw error;

    const total = notificacoes?.length || 0;
    const respondidos = notificacoes?.filter(n => n.respondida).length || 0;

    return {
      total_destinatarios: total,
      total_respondidos: respondidos,
      taxa_resposta: total > 0 ? (respondidos / total) * 100 : 0,
    };
  };

  // Criar nova distribuição
  const { mutateAsync: distribuirFormulario, isPending: isDistribuindo } = useMutation({
    mutationFn: async (params: DistribuicaoParams) => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Usuário não autenticado');

      // Buscar client_id do usuário
      const { data: userProfile } = await supabase
        .from('users')
        .select('client_id')
        .eq('id', userData.user.id)
        .single();

      // Chamar edge function para processar a distribuição
      const { data, error } = await supabase.functions.invoke('distribuir-formulario', {
        body: {
          formulario_id: params.formularioId,
          tipo_destinatario: params.tipoDestinatario,
          destinatario_id: params.destinatarioId,
          destinatario_role: params.destinatarioRole,
          obrigatorio: params.obrigatorio,
          data_limite: params.dataLimite?.toISOString(),
          mensagem_personalizada: params.mensagemPersonalizada,
          client_id: userProfile?.client_id,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['formulario-distribuicoes'] });
      toast.success(`Formulário distribuído para ${data.total_notificacoes} usuário(s)`);
    },
    onError: (error: any) => {
      console.error('Erro ao distribuir formulário:', error);
      toast.error(error.message || 'Erro ao distribuir formulário');
    },
  });

  // Reenviar notificações de uma distribuição
  const { mutateAsync: reenviarNotificacoes, isPending: isReenviando } = useMutation({
    mutationFn: async (distribuicaoId: string) => {
      // Buscar notificações não respondidas
      const { data: notificacoes, error: notifError } = await supabase
        .from('formulario_notificacoes')
        .select('usuario_id')
        .eq('distribuicao_id', distribuicaoId)
        .eq('respondida', false);

      if (notifError) throw notifError;

      // Atualizar flag 'lida' para false para reenviar
      const { error: updateError } = await supabase
        .from('formulario_notificacoes')
        .update({ lida: false, lida_em: null })
        .eq('distribuicao_id', distribuicaoId)
        .eq('respondida', false);

      if (updateError) throw updateError;

      return notificacoes?.length || 0;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ['formulario-distribuicoes'] });
      toast.success(`Notificação reenviada para ${count} usuário(s)`);
    },
    onError: (error: any) => {
      console.error('Erro ao reenviar notificações:', error);
      toast.error('Erro ao reenviar notificações');
    },
  });

  // Desativar distribuição
  const { mutateAsync: desativarDistribuicao, isPending: isDesativando } = useMutation({
    mutationFn: async (distribuicaoId: string) => {
      const { error } = await supabase
        .from('formulario_distribuicoes')
        .update({ ativo: false })
        .eq('id', distribuicaoId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formulario-distribuicoes'] });
      toast.success('Distribuição desativada');
    },
    onError: (error: any) => {
      console.error('Erro ao desativar distribuição:', error);
      toast.error('Erro ao desativar distribuição');
    },
  });

  return {
    distribuicoes: distribuicoes || [],
    isLoading,
    distribuirFormulario,
    isDistribuindo,
    reenviarNotificacoes,
    isReenviando,
    desativarDistribuicao,
    isDesativando,
    getEstatisticas,
  };
}
